import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  Typography,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  Grid,
  TablePagination,
  Switch,
  FormControlLabel,
  MenuItem,
} from '@mui/material';
import {
  Edit,
  Delete,
  Add,
  Search,
  FilterList,
  Restore,
  AttachMoney,
} from '@mui/icons-material';
import api from '../../services/api';

/**
 * Componente de Gestión de Tarifas con Filtros
 * Path: backoffice-web/src/pages/Tarifas/Tarifas.js
 * Última modificación: 2025-12-31 00:30
 * Dev: cpalacios01@gmail.com | +595994648273
 */

export default function Tarifas() {
  const [tarifas, setTarifas] = useState([]);
  const [filteredTarifas, setFilteredTarifas] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTarifa, setEditingTarifa] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Filtros y búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('todas');
  const [mostrarEliminadas, setMostrarEliminadas] = useState(false);
  
  // Paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Estado del formulario
  const [formData, setFormData] = useState({
    nombre: '',
    monto: '',
    distancia_km: '',
    descripcion: '',
    activo: true,
  });

  useEffect(() => {
    fetchTarifas();
  }, [mostrarEliminadas]);

  useEffect(() => {
    applyFilters();
  }, [tarifas, searchTerm, estadoFilter]);

  const fetchTarifas = async () => {
    try {
      const endpoint = mostrarEliminadas ? '/tarifas/all' : '/tarifas';
      const response = await api.get(endpoint);
      setTarifas(response.data);
    } catch (error) {
      console.error('Error al cargar tarifas:', error);
      setError('Error al cargar las tarifas');
    }
  };

  const applyFilters = () => {
    let filtered = [...tarifas];

    // Filtro de búsqueda
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(tarifa => 
        (tarifa.nombre?.toLowerCase() || '').includes(search) ||
        (tarifa.descripcion?.toLowerCase() || '').includes(search)
      );
    }

    // Filtro de estado
    if (estadoFilter === 'activa') {
      filtered = filtered.filter(tarifa => tarifa.activo === true || tarifa.activo === 1);
    } else if (estadoFilter === 'inactiva') {
      filtered = filtered.filter(tarifa => tarifa.activo === false || tarifa.activo === 0);
    }

    setFilteredTarifas(filtered);
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleEstadoFilterChange = (e) => {
    setEstadoFilter(e.target.value);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setEstadoFilter('todas');
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      monto: '',
      distancia_km: '',
      descripcion: '',
      activo: true,
    });
  };

  const handleOpenDialog = (tarifa = null) => {
    if (tarifa) {
      setEditingTarifa(tarifa);
      setFormData({
        nombre: tarifa.nombre || '',
        monto: tarifa.monto || '',
        distancia_km: tarifa.distancia_km || '',
        descripcion: tarifa.descripcion || '',
        activo: tarifa.activo ?? true,
      });
    } else {
      setEditingTarifa(null);
      resetForm();
    }
    setOpenDialog(true);
    setError('');
    setSuccess('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTarifa(null);
    resetForm();
    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const dataToSend = {
        ...formData,
        monto: parseFloat(formData.monto),
        distancia_km: formData.distancia_km ? parseFloat(formData.distancia_km) : null,
      };

      if (editingTarifa) {
        await api.put(`/tarifas/${editingTarifa.id}`, dataToSend);
        setSuccess('Tarifa actualizada exitosamente');
      } else {
        await api.post('/tarifas', dataToSend);
        setSuccess('Tarifa creada exitosamente');
      }

      await fetchTarifas();
      setTimeout(() => {
        handleCloseDialog();
      }, 1500);

    } catch (error) {
      console.error('Error al guardar tarifa:', error);
      
      if (error.response?.status === 422) {
        const errors = error.response.data.errors;
        const errorMessages = Object.values(errors).flat().join(', ');
        setError(`Error de validación: ${errorMessages}`);
      } else {
        setError(error.response?.data?.message || 'Error al guardar la tarifa');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar esta tarifa?')) {
      return;
    }

    try {
      await api.delete(`/tarifas/${id}`);
      setSuccess('Tarifa eliminada exitosamente');
      fetchTarifas();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error al eliminar tarifa:', error);
      setError(error.response?.data?.message || 'Error al eliminar la tarifa');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleRestore = async (id) => {
    try {
      await api.post(`/tarifas/${id}/restore`);
      setSuccess('Tarifa restaurada exitosamente');
      fetchTarifas();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error al restaurar tarifa:', error);
      setError(error.response?.data?.message || 'Error al restaurar la tarifa');
      setTimeout(() => setError(''), 3000);
    }
  };

  const formatMonto = (monto) => {
    return `Gs. ${parseFloat(monto).toLocaleString('es-PY')}`;
  };

  const paginatedTarifas = filteredTarifas.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Gestión de Tarifas</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Nueva Tarifa
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Filtros y búsqueda */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              placeholder="Buscar por nombre o descripción..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                value={estadoFilter}
                label="Estado"
                onChange={handleEstadoFilterChange}
                startAdornment={
                  <InputAdornment position="start">
                    <FilterList />
                  </InputAdornment>
                }
              >
                <MenuItem value="todas">Todas</MenuItem>
                <MenuItem value="activa">Activas</MenuItem>
                <MenuItem value="inactiva">Inactivas</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={mostrarEliminadas}
                  onChange={(e) => setMostrarEliminadas(e.target.checked)}
                />
              }
              label="Eliminadas"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleClearFilters}
            >
              Limpiar Filtros
            </Button>
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Mostrando {filteredTarifas.length} de {tarifas.length} tarifas
          </Typography>
        </Box>
      </Paper>

      {/* Tabla */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell>Monto</TableCell>
              <TableCell>Distancia (km)</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedTarifas.map((tarifa) => (
              <TableRow key={tarifa.id}>
                <TableCell>{tarifa.id}</TableCell>
                <TableCell>{tarifa.nombre}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AttachMoney sx={{ fontSize: 18, mr: 0.5, color: 'success.main' }} />
                    <Typography variant="body2" fontWeight="medium">
                      {formatMonto(tarifa.monto)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  {tarifa.distancia_km ? `${tarifa.distancia_km} km` : '-'}
                </TableCell>
                <TableCell>{tarifa.descripcion || '-'}</TableCell>
                <TableCell>
                  {tarifa.deleted_at ? (
                    <Chip label="Eliminada" color="error" size="small" />
                  ) : (
                    <Chip
                      label={tarifa.activo ? 'Activa' : 'Inactiva'}
                      color={tarifa.activo ? 'success' : 'default'}
                      size="small"
                    />
                  )}
                </TableCell>
                <TableCell>
                  {!tarifa.deleted_at ? (
                    <>
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenDialog(tarifa)}
                        title="Editar"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(tarifa.id)}
                        title="Eliminar"
                      >
                        <Delete />
                      </IconButton>
                    </>
                  ) : (
                    <IconButton
                      color="success"
                      onClick={() => handleRestore(tarifa.id)}
                      title="Restaurar"
                    >
                      <Restore />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {paginatedTarifas.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {searchTerm || estadoFilter !== 'todas'
                    ? 'No se encontraron tarifas con los filtros aplicados'
                    : 'No hay tarifas registradas'
                  }
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredTarifas.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />
      </TableContainer>

      {/* Diálogo crear/editar tarifa */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          {editingTarifa ? 'Editar Tarifa' : 'Nueva Tarifa'}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          <Box component="form" sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nombre *"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                  placeholder="Ej: Tarifa Estudiantes"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Monto *"
                  name="monto"
                  type="number"
                  value={formData.monto}
                  onChange={handleChange}
                  required
                  placeholder="Ej: 2500"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AttachMoney />
                        <Typography>Gs.</Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Distancia (km)"
                  name="distancia_km"
                  type="number"
                  value={formData.distancia_km}
                  onChange={handleChange}
                  placeholder="Ej: 5"
                  helperText="Opcional"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descripción"
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  placeholder="Descripción de la tarifa"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.activo}
                      onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                    />
                  }
                  label="Tarifa Activa"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !formData.nombre || !formData.monto}
          >
            {loading ? 'Guardando...' : editingTarifa ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// cpalacios01@gmail.com | +595994648273
